import React, { useEffect, useState, useRef, useCallback } from 'react';
import YouTube from 'react-youtube';
import debounce from 'lodash/debounce';

function YouTubeVideo(props) {
  // Extract the id from the url
  let id = '';
  const url = props.url
    .replace(/(>|<)/gi, '')
    .split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
  if (url[2] !== undefined) {
    id = url[2].split(/[^0-9a-z_\-]/i);
    id = id[0];
  } else {
    id = url;
  }

  const iframe = useRef(null);
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const [fetchThisVideo, setFetchThisVideo] = useState(false);
  const [newThumbnail, setNewThumbnail] = useState(null);
  const [isThumbnailTooSmall, setIsThumbnailTooSmall] = useState(null);
  const [wrapperHeight, setWrapperHeight] = useState(null);
  const youTubeThumbnail = useCallback(thumbnail => {
    if (thumbnail !== null) {
      const thumbWidth = thumbnail.naturalWidth;
      const resizeWrapperHeight = debounce(() => {
        setWrapperHeight(thumbnail.offsetWidth * 0.5625);
      }, 100)
      setIsThumbnailTooSmall(thumbWidth < 320);
      resizeWrapperHeight();
      window.addEventListener('resize', resizeWrapperHeight);
    }
  }, []);

  const sizes = [
    'sddefault',
    'hqdefault',
    'mqdefault',
    'default'
  ];
  const thumbnailSmall = 'mqdefault';
  const thumbnailLarge = 'maxresdefault';
  const fallbackTitleText = 'Video not working due to cookie settings';
  const fallbackButtonText = 'Enable YouTube Cookies';
  const fallbackLinkText = 'Click here to watch on YouTube';

  const enableCookies = () => {
    if (window.CookieControl) {
      window.CookieControl.changeCategory(2, true);
    } else {
      window.dispatchEvent(new Event('enableYouTube'));
    }
  };

  const toggleCookiesAccepted = (e, acceptCookies) => {
    if (!e) {
      setCookiesAccepted(acceptCookies);
      return;
    }

    if (e?.type) {
      setCookiesAccepted(e.type === 'enableYouTube');

      if (e.type === 'disableYouTube') {
        iframe?.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "stopVideo",
          }),
          "*"
        );
      }
    }
  };

  // Initialise cookie state
  useEffect(() => {
    toggleCookiesAccepted(null, document.body.dataset.youtubeCookiesAccepted === 'true');

    window.addEventListener('enableYouTube', toggleCookiesAccepted);
    window.addEventListener('disableYouTube', toggleCookiesAccepted);

    return () => {
      window.removeEventListener('enableYouTube', toggleCookiesAccepted);
      window.removeEventListener('disableYouTube', toggleCookiesAccepted);
    }
  });

  // Get best available YouTube thumbnail
  useEffect(() => {
    if (isThumbnailTooSmall) {
      const indexOfCurrentThumbnail = sizes.indexOf(newThumbnail);
      const indexOfNextThumbnail = indexOfCurrentThumbnail === -1 ? 0 : indexOfCurrentThumbnail + 1;
      const nextSizeToTry = sizes[indexOfNextThumbnail];
      if (indexOfNextThumbnail < sizes.length) {
        setNewThumbnail(nextSizeToTry);
      }
    }
  }, [isThumbnailTooSmall]);

  const youtubeOptions = {
    height: '390',
    width: '640',
    host: 'https://www.youtube-nocookie.com',
    playerVars: {
      autoplay: 1,
      modestbranding: 1,
      rel: 0
    }
  };

  const onPlayerPlay = e => {
    if (e?.target?.h?.id) {
      const iframeElement = document.querySelector(`iframe[id="${e.target.h.id}"]`);
      iframeElement?.dispatchEvent(new CustomEvent('youtubeVideoPlay', { bubbles: true }));
    }
  };

  return (
    <div className="youtube-video ugc-youtube-video">
      {
        cookiesAccepted &&
        <figure className="youtube-video__content">
          {
            fetchThisVideo ?
              <YouTube
                videoId={id}
                className="youtube-video__iframe"
                opts={youtubeOptions}
                onPlay={onPlayerPlay}/>
              :
              <a
                className="youtube-video__link"
                href={`https://www.youtube.com/watch?v=${id}`}
                aria-label="Play YouTube video"
                onClick={e => {
                  e.preventDefault();
                  setFetchThisVideo(true);
                }}
                itemProp="video">
                <div
                  data-youtube-id={id}
                  style={{ 'height': wrapperHeight ? `${wrapperHeight}px` : 'auto' }}>
                  <picture>
                    <source
                      type="image/webp"
                      srcSet={
                        newThumbnail ?
                          `https://img.youtube.com/vi_webp/${id}/${newThumbnail}.webp` :
                          `https://img.youtube.com/vi_webp/${id}/${thumbnailSmall}.webp 320w, https://img.youtube.com/vi_webp/${id}/${thumbnailLarge}.webp 1280w`
                      }
                      sizes="(min-width: 376px) 100vw, 320px"/>
                    <source
                      srcSet={
                        newThumbnail ?
                          `https://img.youtube.com/vi/${id}/${newThumbnail}.jpg` :
                          `https://img.youtube.com/vi/${id}/${thumbnailSmall}.jpg 320w, https://img.youtube.com/vi/${id}/${thumbnailLarge}.jpg 1280w`
                      }
                      sizes="(min-width: 376px) 100vw, 320px"/>
                    <img
                      ref={youTubeThumbnail}
                      src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
                      style={newThumbnail === 'sddefault' ? { 'objectFit': 'cover', 'maxHeight': '100%' } : {}}
                      alt="Video placeholder"/>
                  </picture>
                </div>
                <div className="icon-container">
                  <span className="icon icon--play"/>
                </div>
              </a>
          }
        </figure>
      }

      {
        !cookiesAccepted &&
        <div className="youtube-video__cookies-revoked-placeholder">
          <div className="youtube-video__cookies-revoked-placeholder-content">
            <span className="icon icon--alert ugc-youtube-video__icon-alert"/>
              <span className="youtube-video__cookies-revoked-placeholder-title ugc-youtube-video__cookies-revoked-placeholder-title">
            {fallbackTitleText}
          </span>
            <button
                className="youtube-video__cookies-revoked-placeholder-button ugc-youtube-video__cookies-revoked-placeholder-button govuk-button he-button he-button__secondary"
              aria-label="Enable YouTube cookies and show this video"
              onClick={enableCookies}>
              {fallbackButtonText}
            </button>
            <span
                className="youtube-video__cookies-revoked-placeholder-link-wrapper ugc-youtube-video__cookies-revoked-placeholder-link-wrapper">
            or&nbsp;
              <a href={`https://www.youtube.com/watch?v=${id}`}>
              {fallbackLinkText}
            </a>
          </span>
          </div>
        </div>
      }

    </div>
  );
}

export default YouTubeVideo;
